<script lang="ts" setup>
import DetailInformation from '@/common/components/molecules/DetailInformation.vue';
import { useInternational } from '@/common/locale';
import type { RuleGroup } from './eventAlertInformation.types';
import { isTextItem, isListItem, isMultiTextItem } from './eventAlertInformation.utils';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';

interface Props {
  ruleGroups: RuleGroup[];
}

defineProps<Props>();

const { t } = useInternational();
</script>

<template>
  <detail-information
    :title="t('WORD.RULE_DATA')"
    use-expand
    :expand-env-key="USER_ENV_KEYS.TOGGLE_alert_eventAlertDetail_ruleData_expanded"
    class="event-alert-rule-data"
  >
    <template #content>
      <v-stack
        :spacing="8"
        class="event-alert-rule-data__content"
      >
        <h-stack
          v-for="(group, gIdx) in ruleGroups"
          :key="gIdx"
          :spacing="10"
          class="event-alert-rule-data__content-items"
        >
          <template
            v-for="(item, iIdx) in group.items"
            :key="iIdx"
          >
            <h-stack
              v-if="isTextItem(item) && item.kind === 'text'"
              :spacing="8"
              class="event-alert-rule-data__field"
            >
              <span>{{ item.label }}</span>
              <span>{{ item.value }}</span>
            </h-stack>
            <h-stack
              v-else-if="isTextItem(item) && item.kind === 'labelChip'"
              :spacing="8"
              class="event-alert-rule-data__field"
            >
              <span>{{ item.label }}</span>
              <span class="event-alert-rule-data__content-item-label">
                {{ item.value }}
              </span>
            </h-stack>
            <h-stack
              v-else-if="isListItem(item)"
              class="event-alert-rule-data__list-text"
              :spacing="8"
            >
              <span>{{ item.label }}</span>
              <span
                class="event-alert-rule-data__value-text"
                :title="item.list.join(', ')"
              >
                {{ item.list.join(', ') }}
              </span>
            </h-stack>
            <h-stack
              v-else-if="isMultiTextItem(item)"
              class="event-alert-rule-data__multi-text"
              :spacing="8"
            >
              <span>{{ item.label }}</span>
              <span
                v-for="(v, mi) in item.texts"
                :key="mi"
                class="event-alert-rule-data__value-text"
                :title="String(v)"
              >
                {{ v }}
              </span>
            </h-stack>
            <separator
              v-if="iIdx < group.items.length - 1"
              class="event-alert-rule-data__separator"
            />
          </template>
        </h-stack>
      </v-stack>
    </template>
  </detail-information>
</template>

<style scoped lang="scss">
.event-alert-rule-data {
  :deep(.detail-information__content) {
    padding: 12px;
  }

  &__separator {
    width: 1px;
    height: 12px;
    background-color: var(--color-gray-05-05);
  }

  &__content-items {
    flex-wrap: wrap;
    row-gap: 4px;
    align-items: center;
    min-width: 0;
    min-height: 20px;
  }

  &__field {
    flex-shrink: 0;
    white-space: nowrap;

    span:nth-of-type(1) {
      font-weight: 500;
    }
  }

  &__content-item-label {
    padding: 2px 6px;
    border: 1px solid var(--color-gray-04-06);
    border-radius: 2px;
    background-color: var(--color-gray-06-01);
    font-weight: 500;
  }

  &__list-text,
  &__multi-text {
    flex: 1 1 200px;
    align-items: center;
    min-width: 0;
    max-width: 100%;

    > span:first-of-type {
      flex-shrink: 0;
      font-weight: 500;
      white-space: nowrap;
    }
  }

  &__value-text {
    min-width: 0;
    white-space: nowrap;

    @include shortening();
  }
}
</style>
